import React, { memo } from 'react'

import { Button, Icon, ChatAI } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { VERSION_KIND } from 'Pods/ThreeDStudio/Components/ImageAssistantPanel/imageRun'
import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import Composer from 'Pods/ThreeDStudio/Shared/Composer/Composer'
import DownloadButton from 'Pods/ThreeDStudio/Shared/DownloadButton/DownloadButton'

import { EMPTY_ARRAY, NOOP } from 'Utils/stableDefaults'

import MaskCanvas from './Components/Canvas/MaskCanvas'
import HistoryControl from './Components/HistoryControl/HistoryControl'
import PromptViewer from './Components/PromptViewer/PromptViewer'
import Toolbar from './Components/Toolbar/Toolbar'
import useEditCanvas from './useEditCanvas'

const generateNewIcon = <Icon svgComponent={ChatAI} size={iconSize} notSpaced color="darker" />

// Holds the live editing session. Remounted (via the key in EditTab) whenever a
// new image is loaded, so its seeded state resets cleanly; stays mounted across
// tab switches so the work is preserved.
function EditCanvas() {
  const t = useTranslation()
  const {
    canvas,
    currentImage,
    versions,
    versionCount,
    prompt,
    attachments,
    isHistoryOpen,
    isSending,
    isSendDisabled,
    isUploadDisabled,
    fileInputRef,
    handlePromptChange,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleToggleHistory,
    handleCloseHistory,
    handleSelectVersion,
    handleStartNew,
  } = useEditCanvas()

  return (
    <div className="image-assistant-edit-tab__edit">
      <div className="image-assistant-edit-tab__edit-scroll">
        <div className="image-assistant-edit-tab__header">
          <Button
            type="secondary"
            size="small"
            variant="with-icon"
            actionType="button"
            customClass="image-assistant-edit-tab__generate-new"
            icon={generateNewIcon}
            onClick={handleStartNew}
          >
            {t('imageAssistantPanel.editTab.generateNew')}
          </Button>
          {currentImage && (
            <DownloadButton
              result={currentImage}
              customClass="image-assistant-edit-tab__download"
              isIconOnly
            />
          )}
        </div>
        <div className="image-assistant-edit-tab__canvas-card">
          <MaskCanvas
            image={currentImage}
            canvasRef={canvas.canvasRef}
            containerRef={canvas.containerRef}
            brushSize={canvas.brushSize}
            isErasing={canvas.isErasing}
            isSending={isSending}
            overlay={(
              <>
                <HistoryControl
                  versions={versions}
                  versionCount={versionCount}
                  currentId={currentImage?.id}
                  isOpen={isHistoryOpen}
                  isSending={isSending}
                  onToggle={handleToggleHistory}
                  onSelectVersion={handleSelectVersion}
                  onClose={handleCloseHistory}
                />
                {currentImage?.prompt && currentImage.kind === VERSION_KIND.PROMPT
                  ? <PromptViewer key={currentImage.id} prompt={currentImage.prompt} />
                  : null}
              </>
            )}
            onPointerDown={canvas.handlePointerDown}
            onPointerMove={canvas.handlePointerMove}
            onPointerUp={canvas.handlePointerUp}
          />
        </div>
      </div>
      <Composer
        prompt={prompt}
        attachments={attachments}
        tags={EMPTY_ARRAY}
        fileInputRef={fileInputRef}
        sendLabel={t('imageAssistantPanel.actions.editImage')}
        placeholder={t('imageAssistantPanel.editTab.composer.placeholder')}
        tools={(
          <Toolbar
            canUndo={canvas.canUndo}
            canRedo={canvas.canRedo}
            isErasing={canvas.isErasing}
            brushSize={canvas.brushSize}
            isSending={isSending}
            onUndo={canvas.undo}
            onRedo={canvas.redo}
            onErasingChange={canvas.setErasing}
            onBrushSizeChange={canvas.setBrushSize}
          />
        )}
        hasMobileToolRow
        isSendDisabled={isSendDisabled}
        isUploadDisabled={isUploadDisabled}
        isUploadHidden
        onPromptChange={handlePromptChange}
        onTagClick={NOOP}
        onUploadClick={handleUploadClick}
        onRemoveAttachment={handleRemoveAttachment}
        onFilesChange={handleFilesChange}
        onSend={handleSend}
      />
    </div>
  )
}

export default memo(EditCanvas)
